import Route from '@ember/routing/route';
import { camelize } from '@ember/string';
import type RouterService from '@ember/routing/router';
import { service } from '@ember/service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type PartnerService from 'tio-employee/services/partner';
import type FeaturesService from 'tio-common/services/features';
export default class AuthenticatedWellnessInsightsIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare partner: PartnerService;
  @service declare features: FeaturesService;

  queryParams = {
    accountCreationFeedbackActive: { refreshModel: false, replace: true },
  };

  beforeModel() {
    // Redirect if TA-only context
    if (this.partner.isTaOnly) {
      this.router.transitionTo('authenticated.dashboard');
    }
  }

  async model() {
    // Get wellness data from parent authenticated route
    const authenticatedModel = this.modelFor('authenticated') as {
      wellnessData: Record<string, unknown> | null;
      user: { id: string };
    };

    const { wellnessData, user } = authenticatedModel;
    if (!wellnessData) {
      return null;
    }

    // The /users/:id/user-profile?profile_key=...WELLNESS_TRAIN response is a
    // FLAT, snake_case object (not JSON:API) with a `components` array. The
    // legacy wellness-train serializer normalized it; that serializer is gone,
    // so build the JSON:API document by hand and store.push it (no serializer
    // needed). Contract reproduced from the old serializer: camelize the train
    // attributes; split `components` into wellness-insights-component-definition
    // records (sub_title -> subtitle, synthetic per-type id, `order` index) and
    // link them via the componentDefinitions hasMany; set the `user` belongsTo
    // by linkage only (the user record is already in the store from the parent
    // route — do not fabricate one).
    const recordId = `user-${user.id}-wellness-train`;
    const { components, ...trainAttrs } = wellnessData as {
      components?: Array<Record<string, unknown>>;
      [key: string]: unknown;
    };

    const attributes: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(trainAttrs)) {
      attributes[camelize(key)] = value;
    }

    const definitions = (components ?? []).map((component, order) => {
      const defAttributes: Record<string, unknown> = { order };
      for (const [key, value] of Object.entries(component)) {
        if (key === 'sub_title') {
          defAttributes['subtitle'] = value;
        } else if (key === 'button') {
          // `button` is not a wellness-insights-component-definition schema
          // field — drop it (the legacy serializer's value was ignored too).
        } else {
          defAttributes[camelize(key)] = value;
        }
      }
      return {
        type: 'wellness-insights-component-definition',
        id: `user-${user.id}-wellness-train-component-definition-${String(component['type'])}`,
        attributes: defAttributes,
        relationships: {
          wellnessTrain: { data: { type: 'wellness-train', id: recordId } },
        },
      };
    });

    this.store.push({
      data: {
        type: 'wellness-train',
        id: recordId,
        attributes,
        relationships: {
          user: { data: { type: 'user', id: user.id } },
          componentDefinitions: {
            data: definitions.map((def) => ({ type: def.type, id: def.id })),
          },
        },
      },
      included: definitions,
    } as unknown as Parameters<StoreService['push']>[0]);

    return this.store.peekRecord('wellness-train', recordId);
  }
}
